import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20PageResponse } from '@/shared/api/normalize-pro20-page';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  CreateStripeBookingDepositIntentParams,
  CreateStripeBookingTransactionParams,
  CreateStripeSetupIntentParams,
  ReconcileStripeBookingDepositParams,
  RefundStripeDepositParams,
  SendStripeDepositRequestCtaParams,
  StripeAccountDetails,
  StripeBookingDepositIntent,
  StripeConnectStatus,
  StripeDepositReconcileResult,
  StripePaymentPolicy,
  StripeSetupIntentResponse,
  StripeSetupIntentStatus,
  StripeTransaction,
  StripeTransactionListParams,
  StripeTransactionSummary,
  UpdateStripePaymentPolicyParams,
} from '../types/stripe.types';
import {
  normalizeStripeAccountDetails,
  normalizeStripeBookingDepositIntent,
  normalizeStripeConnectStatus,
  normalizeStripeDepositReconcile,
  normalizeStripePaymentPolicy,
  normalizeStripeSetupIntent,
  normalizeStripeSetupIntentStatus,
  normalizeStripeTransaction,
  normalizeStripeTransactionList,
  normalizeStripeTransactionSummary,
} from '../utils/normalize-stripe';

const CONNECT_BASE = '/payment/stripe/connect/branches';

function branchUrl(branchCode: string, path: string): string {
  return `${CONNECT_BASE}/${encodeURIComponent(branchCode)}${path}`;
}

function buildTransactionsQuery({
  page,
  size,
  status,
}: Omit<StripeTransactionListParams, 'branchCode'>): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  params.set('sort', 'createdAt,desc');

  if (status) {
    params.set('status', status);
  }

  return `?${params.toString()}`;
}

export const stripeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Safe for any branch — call this before `/details`, which needs an account. */
    getStripeConnectStatus: builder.query<StripeConnectStatus, string>({
      query: (branchCode) => ({
        url: branchUrl(branchCode, '/status'),
        method: 'GET',
      }),
      transformResponse: (response: unknown, _meta, branchCode) =>
        normalizeStripeConnectStatus(
          normalizePro20Response(response),
          branchCode,
        ),
      providesTags: (_result, _error, branchCode) => [
        { type: API_TAGS.StripeAccount, id: `${branchCode}-status` },
      ],
    }),

    getStripeAccountDetails: builder.query<StripeAccountDetails, string>({
      query: (branchCode) => ({
        url: branchUrl(branchCode, '/details'),
        method: 'GET',
      }),
      transformResponse: (response: unknown, _meta, branchCode) =>
        normalizeStripeAccountDetails(
          normalizePro20Response(response),
          branchCode,
        ),
      providesTags: (_result, _error, branchCode) => [
        { type: API_TAGS.StripeAccount, id: branchCode },
      ],
    }),

    getStripePaymentPolicy: builder.query<StripePaymentPolicy, string>({
      query: (branchCode) => ({
        url: branchUrl(branchCode, '/payment-policy'),
        method: 'GET',
      }),
      transformResponse: (response: unknown, _meta, branchCode) =>
        normalizeStripePaymentPolicy(
          normalizePro20Response(response),
          branchCode,
        ),
      providesTags: (_result, _error, branchCode) => [
        { type: API_TAGS.StripeAccount, id: `${branchCode}-policy` },
      ],
    }),

    /** Saving always creates a new policy version on the backend. */
    updateStripePaymentPolicy: builder.mutation<
      StripePaymentPolicy,
      UpdateStripePaymentPolicyParams
    >({
      query: ({ branchCode, content }) => ({
        url: branchUrl(branchCode, '/payment-policy'),
        method: 'PUT',
        body: { content },
      }),
      transformResponse: (response: unknown, _meta, { branchCode }) =>
        normalizeStripePaymentPolicy(
          normalizePro20Response(response),
          branchCode,
        ),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.StripeAccount, id: `${branchCode}-policy` },
      ],
    }),

    getStripeTransactions: builder.query<
      PaginatedResponse<StripeTransaction>,
      StripeTransactionListParams
    >({
      query: ({ branchCode, page, size, status }) => ({
        url: branchUrl(
          branchCode,
          `/transactions${buildTransactionsQuery({ page, size, status })}`,
        ),
        method: 'GET',
      }),
      transformResponse: (response: unknown, _meta, arg) =>
        normalizePro20PageResponse(
          normalizePro20Response(response),
          normalizeStripeTransaction,
          arg.size,
        ),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.StripePayments, id: branchCode },
      ],
    }),

    getStripeTransactionSummary: builder.query<
      StripeTransactionSummary,
      string
    >({
      query: (branchCode) => ({
        url: branchUrl(branchCode, '/transactions/summary'),
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        normalizeStripeTransactionSummary(normalizePro20Response(response)),
      providesTags: (_result, _error, branchCode) => [
        { type: API_TAGS.StripePayments, id: `${branchCode}-summary` },
      ],
    }),

    /** Charges a held authorization. */
    captureStripeDeposit: builder.mutation<
      StripeTransaction | null,
      { depositCode: string; branchCode: string }
    >({
      query: ({ depositCode }) => ({
        url: `${CONNECT_BASE}/deposits/${encodeURIComponent(depositCode)}/capture`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) =>
        normalizeStripeTransaction(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.StripePayments, id: branchCode },
        { type: API_TAGS.StripePayments, id: `${branchCode}-summary` },
        { type: API_TAGS.Bookings, id: branchCode },
      ],
    }),

    /** Cancels a held authorization and frees the funds. */
    releaseStripeDeposit: builder.mutation<
      StripeTransaction | null,
      { depositCode: string; branchCode: string }
    >({
      query: ({ depositCode }) => ({
        url: `${CONNECT_BASE}/deposits/${encodeURIComponent(depositCode)}/release`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) =>
        normalizeStripeTransaction(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.StripePayments, id: branchCode },
        { type: API_TAGS.StripePayments, id: `${branchCode}-summary` },
        { type: API_TAGS.Bookings, id: branchCode },
      ],
    }),

    getStripeBookingTransactions: builder.query<
      StripeTransaction[],
      { branchCode: string; bookingCode: string }
    >({
      query: ({ branchCode, bookingCode }) => ({
        url: branchUrl(
          branchCode,
          `/bookings/${encodeURIComponent(bookingCode)}/transactions`,
        ),
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        normalizeStripeTransactionList(normalizePro20Response(response)),
      providesTags: (_result, _error, { branchCode, bookingCode }) => [
        { type: API_TAGS.StripePayments, id: `${branchCode}-${bookingCode}` },
        { type: API_TAGS.StripePayments, id: branchCode },
      ],
    }),

    createStripeBookingTransaction: builder.mutation<
      StripeTransaction | null,
      CreateStripeBookingTransactionParams
    >({
      query: ({
        branchCode,
        bookingCode,
        amountInMinorUnits,
        currency,
        description,
        mode,
        applicationFeeInMinorUnits = 0,
      }) => ({
        url: branchUrl(
          branchCode,
          `/bookings/${encodeURIComponent(bookingCode)}/transactions`,
        ),
        method: 'POST',
        body: {
          amountInMinorUnits,
          currency,
          ...(description ? { description } : {}),
          mode,
          applicationFeeInMinorUnits,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeStripeTransaction(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { branchCode, bookingCode }) => [
        { type: API_TAGS.StripePayments, id: `${branchCode}-${bookingCode}` },
        { type: API_TAGS.StripePayments, id: branchCode },
        { type: API_TAGS.StripePayments, id: `${branchCode}-summary` },
        { type: API_TAGS.Bookings, id: branchCode },
      ],
    }),

    refundStripeDeposit: builder.mutation<
      StripeTransaction | null,
      RefundStripeDepositParams
    >({
      query: ({ depositCode, amountInMinorUnits, reason, requestCode }) => ({
        url: `${CONNECT_BASE}/deposits/${encodeURIComponent(depositCode)}/refunds`,
        method: 'POST',
        body: { amountInMinorUnits, reason, requestCode },
      }),
      transformResponse: (response: unknown) =>
        normalizeStripeTransaction(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.StripePayments, id: branchCode },
        { type: API_TAGS.StripePayments, id: `${branchCode}-summary` },
        { type: API_TAGS.Bookings, id: branchCode },
      ],
    }),

    sendStripeDepositRequestCta: builder.mutation<
      null,
      SendStripeDepositRequestCtaParams
    >({
      query: ({
        bookingCode,
        depositRequestLink,
        amountInMinorUnits,
        currency = 'eur',
      }) => ({
        url: `/payment/deposit/requestdetailsbybookingcode/${encodeURIComponent(bookingCode)}`,
        method: 'POST',
        body: { depositRequestLink, amountInMinorUnits, currency },
      }),
      transformResponse: () => null,
    }),

    createStripeSetupIntent: builder.mutation<
      StripeSetupIntentResponse,
      CreateStripeSetupIntentParams
    >({
      query: ({ branchCode, prefix, phone, email }) => ({
        url: '/payment/stripe/setup-intent',
        method: 'POST',
        body: {
          branchCode,
          prefix,
          phone,
          ...(email ? { email } : {}),
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeStripeSetupIntent(normalizePro20Response(response)),
    }),

    getStripeSetupIntentStatus: builder.query<StripeSetupIntentStatus, string>({
      query: (flowCode) => ({
        url: `/payment/stripe/setup-intent/${encodeURIComponent(flowCode)}/status`,
        method: 'GET',
      }),
      transformResponse: (response: unknown, _meta, flowCode) =>
        normalizeStripeSetupIntentStatus(
          normalizePro20Response(response),
          flowCode,
        ),
    }),

    createStripeBookingDepositIntent: builder.mutation<
      StripeBookingDepositIntent,
      CreateStripeBookingDepositIntentParams
    >({
      query: ({
        branchCode,
        bookingCode,
        amountInMinorUnits,
        currency,
        description,
        applicationFeeInMinorUnits = 0,
        mode,
      }) => ({
        url: branchUrl(
          branchCode,
          `/bookings/${encodeURIComponent(bookingCode)}/deposits/intent`,
        ),
        method: 'POST',
        body: {
          amountInMinorUnits,
          currency,
          description,
          applicationFeeInMinorUnits,
          mode,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeStripeBookingDepositIntent(normalizePro20Response(response)),
    }),

    reconcileStripeBookingDeposit: builder.mutation<
      StripeDepositReconcileResult,
      ReconcileStripeBookingDepositParams
    >({
      query: ({ branchCode, bookingCode, depositCode }) => ({
        url: branchUrl(
          branchCode,
          `/bookings/${encodeURIComponent(bookingCode)}/deposits/${encodeURIComponent(depositCode)}/reconcile`,
        ),
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) =>
        normalizeStripeDepositReconcile(normalizePro20Response(response)),
    }),
  }),
});

export const {
  useGetStripeConnectStatusQuery,
  useGetStripeAccountDetailsQuery,
  useGetStripePaymentPolicyQuery,
  useUpdateStripePaymentPolicyMutation,
  useGetStripeTransactionsQuery,
  useGetStripeTransactionSummaryQuery,
  useCaptureStripeDepositMutation,
  useReleaseStripeDepositMutation,
  useGetStripeBookingTransactionsQuery,
  useLazyGetStripeBookingTransactionsQuery,
  useCreateStripeBookingTransactionMutation,
  useRefundStripeDepositMutation,
  useSendStripeDepositRequestCtaMutation,
  useCreateStripeSetupIntentMutation,
  useGetStripeSetupIntentStatusQuery,
  useLazyGetStripeSetupIntentStatusQuery,
  useCreateStripeBookingDepositIntentMutation,
  useReconcileStripeBookingDepositMutation,
} = stripeApi;
